import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  XMarkIcon,
  ChartBarIcon,
  FireIcon,
  TruckIcon,
  UserIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  BuildingStorefrontIcon,
  EyeIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { fuelTransactionsService } from '../../services/logistics/fuel-transactions';
import { driversService } from '../../services/logistics/drivers';
import { shipmentsService } from '../../services/logistics/shipments';
import { fuelAnalyticsService } from '../../services/logistics/fuel-analytics';
import { geoService } from '../../services/system/geo-service';
import { getFraudFlagDisplayName, getFraudScoreColor, getTransactionSeverity } from '../../utils/fuel-fraud-detection';
import LoadingSpinner from '../ui/LoadingSpinner';
import { formatNumber } from '../../utils/formatting';
import toast from 'react-hot-toast';

const FuelAnalyticsModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  const [loading, setLoading] = useState(true);
  const [fuelTransactions, setFuelTransactions] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showSuspiciousOnly, setShowSuspiciousOnly] = useState(false);
  const [analytics, setAnalytics] = useState({
    totalTransactions: 0,
    totalCost: 0,
    totalLiters: 0,
    averageCostPerLiter: 0,
    vehicleStats: {},
    stationStats: {},
    monthlyStats: {},
    topCostPerKm: [],
    suspiciousTransactionsCount: 0,
    enrichedTransactions: []
  });
  const [deleting, setDeleting] = useState(null);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      loadData();
    }
  }, [isOpen, currentCompany?.id]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load all fuel transactions, drivers, and shipments
      const [transactions, driversData, shipmentsData] = await Promise.all([
        fuelTransactionsService.getFuelTransactionsByCompany(currentCompany.id),
        driversService.getDrivers(currentCompany.id).catch((error) => {
          console.warn('Failed to load drivers:', error);
          return [];
        }),
        shipmentsService.getShipments(currentCompany.id).catch((error) => {
          console.warn('Failed to load shipments:', error);
          return [];
        })
      ]);

      setFuelTransactions(transactions || []);
      setDrivers(driversData || []);
      setShipments(shipmentsData || []);

      // Calculate enhanced analytics with fraud detection
      const enhancedAnalytics = await fuelAnalyticsService.calculateAnalytics(
        transactions || [], 
        driversData || [], 
        shipmentsData || []
      );
      setAnalytics(enhancedAnalytics);
    } catch (error) {
      console.error('Error loading fuel analytics:', error);

      // Provide more specific error messages
      let errorMessage = 'Failed to load fuel analytics';
      if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      if (error.code) {
        errorMessage += ` (Code: ${error.code})`;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // This function is now replaced by fuelAnalyticsService.calculateAnalytics

  // Filter transactions by selected vehicle and suspicious flag
  let filteredTransactions = analytics.enrichedTransactions || [];
  
  if (selectedVehicle !== 'all') {
    filteredTransactions = filteredTransactions.filter(t =>
      (t.mappedVehiclePlate || t.vehicle_plate || 'Unknown Vehicle') === selectedVehicle
    );
  }
  
  if (showSuspiciousOnly) {
    filteredTransactions = filteredTransactions.filter(t => t.fraudScore > 0);
  }

  // Format date for display
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle delete transaction
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المعاملة؟')) {
      return;
    }

    try {
      setDeleting(transactionId);
      await fuelTransactionsService.deleteFuelTransaction(transactionId);

      // Reload data to refresh analytics
      await loadData();
      toast.success('تم حذف المعاملة بنجاح');
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('فشل في حذف المعاملة');
    } finally {
      setDeleting(null);
    }
  };

  // Open Google Maps for station location (enhanced to handle string coordinates)
  const openGoogleMaps = (coordinates, stationName) => {
    const url = geoService.generateGoogleMapsUrl(coordinates);
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('إحداثيات المحطة غير متوفرة');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <ChartBarIcon className="h-6 w-6 text-orange-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              تحليلات الوقود الشاملة
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* No Data Message */}
              {fuelTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <FireIcon className="mx-auto h-16 w-16 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    لا توجد بيانات وقود
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    لم يتم العثور على أي معاملات وقود لهذه الشركة
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                        <div>
                          <div className="text-sm text-blue-600">إجمالي المعاملات</div>
                          <div className="text-2xl font-bold text-blue-900">
                            {analytics.totalTransactions}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <div className="flex items-center space-x-2">
                        <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                        <div>
                          <div className="text-sm text-green-600">إجمالي التكلفة</div>
                          <div className="text-2xl font-bold text-green-900">
                            {formatNumber(analytics.totalCost)} ريال
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <div className="flex items-center space-x-2">
                        <FireIcon className="h-5 w-5 text-orange-600" />
                        <div>
                          <div className="text-sm text-orange-600">إجمالي اللترات</div>
                          <div className="text-2xl font-bold text-orange-900">
                            {formatNumber(analytics.totalLiters)} لتر
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center space-x-2">
                        <ChartBarIcon className="h-5 w-5 text-purple-600" />
                        <div>
                          <div className="text-sm text-purple-600">متوسط السعر/لتر</div>
                          <div className="text-2xl font-bold text-purple-900">
                            {formatNumber(analytics.averageCostPerLiter)} ريال
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <div className="flex items-center space-x-2">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                        <div>
                          <div className="text-sm text-red-600">معاملات مشتبه بها</div>
                          <div className="text-2xl font-bold text-red-900">
                            {analytics.suspiciousTransactionsCount || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <label className="text-sm font-medium text-gray-700">
                        تصفية حسب المركبة:
                      </label>
                      <select
                        value={selectedVehicle}
                        onChange={(e) => setSelectedVehicle(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">جميع المركبات</option>
                        {Object.keys(analytics.vehicleStats).map(vehicle => (
                          <option key={vehicle} value={vehicle}>
                            {vehicle} ({analytics.vehicleStats[vehicle].transactions} معاملة)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center space-x-3">
                      <FunnelIcon className="h-4 w-4 text-gray-500" />
                      <label className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={showSuspiciousOnly}
                          onChange={(e) => setShowSuspiciousOnly(e.target.checked)}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-gray-700">إظهار المعاملات المشتبه بها فقط</span>
                      </label>
                    </div>
                  </div>

                  {/* Analytics Tabs */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Vehicle Statistics */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <TruckIcon className="h-5 w-5 mr-2 text-blue-600" />
                        إحصائيات المركبات
                      </h3>
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {Object.entries(analytics.vehicleStats).map(([vehicle, stats]) => (
                          <div key={vehicle} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 flex items-center">
                                  <TruckIcon className="h-4 w-4 mr-2 text-gray-600" />
                                  {vehicle}
                                  {stats.suspiciousTransactions > 0 && (
                                    <ExclamationTriangleIcon className="h-4 w-4 ml-2 text-red-500" />
                                  )}
                                </div>
                                <div className="text-sm text-blue-600 mr-6">
                                  السائق: {stats.associatedDriver}
                                </div>
                                <div className="text-sm text-gray-600 mr-6">
                                  {stats.transactions} معاملة • {formatNumber(stats.totalLiters)} لتر
                                  {stats.suspiciousTransactions > 0 && (
                                    <span className="text-red-600 ml-2">
                                      • {stats.suspiciousTransactions} مشتبه بها
                                    </span>
                                  )}
                                </div>
                                {/* Enhanced KPIs */}
                                <div className="text-xs text-gray-500 mr-6 mt-1">
                                  {stats.kmDriven > 0 && (
                                    <>
                                      المسافة: {formatNumber(stats.kmDriven)} كم • 
                                      التكلفة/كم: {formatNumber(stats.costPerKm)} ريال • 
                                      الاستهلاك: {formatNumber(stats.lPer100km)} ل/100كم
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-green-600">
                                  {formatNumber(stats.totalCost)} ريال
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatNumber(stats.totalLiters > 0 ? stats.totalCost / stats.totalLiters : 0)} ريال/لتر
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Station Statistics */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <BuildingStorefrontIcon className="h-5 w-5 mr-2 text-green-600" />
                        إحصائيات المحطات
                      </h3>
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {Object.entries(analytics.stationStats).map(([station, stats]) => (
                          <div key={station} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <div className="font-medium text-gray-900">{station}</div>
                                  {stats.coordinates && (
                                    <button
                                      onClick={() => openGoogleMaps(stats.coordinates, station)}
                                      className="text-blue-600 hover:text-blue-800"
                                      title="عرض على الخريطة"
                                    >
                                      <MapPinIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  {stats.suspiciousTransactions > 0 && (
                                    <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                                  )}
                                </div>
                                {/* Location info */}
                                {(stats.city || stats.district) && (
                                  <div className="text-xs text-blue-600">
                                    {stats.city && stats.district ? `${stats.city} • ${stats.district}` : stats.city || stats.district}
                                  </div>
                                )}
                                <div className="text-sm text-gray-600">
                                  {stats.transactions} معاملة • {formatNumber(stats.totalLiters)} لتر
                                  {stats.suspiciousTransactions > 0 && (
                                    <span className="text-red-600 ml-2">
                                      • {stats.suspiciousTransactions} مشتبه بها
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-green-600">
                                  {formatNumber(stats.totalCost)} ريال
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatNumber(stats.totalLiters > 0 ? stats.totalCost / stats.totalLiters : 0)} ريال/لتر
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top 5 Cost per KM */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <ChartBarIcon className="h-5 w-5 mr-2 text-red-600" />
                        أعلى تكلفة/كم (أول 5)
                      </h3>
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {analytics.topCostPerKm && analytics.topCostPerKm.length > 0 ? (
                          analytics.topCostPerKm.map((vehicle, index) => (
                            <div key={vehicle.vehiclePlate} className="bg-gray-50 rounded-lg p-3">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                  <div className="bg-red-100 text-red-800 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                                    {index + 1}
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900">{vehicle.vehiclePlate}</div>
                                    <div className="text-xs text-gray-500">
                                      {formatNumber(vehicle.kmDriven)} كم • {vehicle.transactions} معاملة
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-red-600">
                                    {formatNumber(vehicle.costPerKm)} ريال/كم
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatNumber(vehicle.lPer100km)} ل/100كم
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            لا توجد بيانات مسافات متاحة
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Transactions List */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FireIcon className="h-5 w-5 mr-2 text-orange-600" />
                      معاملات الوقود ({filteredTransactions.length})
                    </h3>

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {filteredTransactions.map((transaction) => {
                        const severity = getTransactionSeverity(transaction.fraudScore || 0);
                        const rowClass = severity === 'high' 
                          ? 'bg-red-50 border border-red-200 hover:bg-red-100'
                          : severity === 'low'
                          ? 'bg-amber-50 border border-amber-200 hover:bg-amber-100'
                          : 'bg-gray-50 hover:bg-gray-100';
                        
                        return (
                        <div
                          key={transaction.id}
                          className={`rounded-lg p-4 transition-colors ${rowClass}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                              {/* Transaction Info */}
                              <div>
                                <div className="font-medium text-gray-900 flex items-center">
                                  #{transaction.invoice_number || 'N/A'}
                                  {transaction.fraudScore >= 5 && (
                                    <ExclamationTriangleIcon className="h-4 w-4 ml-2 text-red-500" />
                                  )}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {formatDateTime(transaction.fill_time)}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {transaction.mappedVehiclePlate || transaction.vehicle_plate}
                                </div>
                              </div>

                              {/* Fuel Details */}
                              <div>
                                <div className="text-sm text-gray-600">الكمية والتكلفة</div>
                                <div className="font-medium text-gray-900">
                                  {formatNumber(transaction.liters)} لتر
                                </div>
                                <div className="font-bold text-green-600">
                                  {formatNumber(transaction.cost)} ريال
                                </div>
                              </div>

                              {/* Driver */}
                              <div>
                                <div className="text-sm text-gray-600">السائق</div>
                                <div className="font-medium text-gray-900">
                                  {transaction.enrichedDriverInfo || transaction.driver || 'غير محدد'}
                                </div>
                              </div>

                              {/* Station */}
                              <div>
                                <div className="text-sm text-gray-600">المحطة</div>
                                <div className="flex items-center space-x-2">
                                  <div className="font-medium text-gray-900 truncate">
                                    {transaction.station_name || 'غير محدد'}
                                  </div>
                                  {transaction.station_coordinates && (
                                    <button
                                      onClick={() => openGoogleMaps(transaction.station_coordinates, transaction.station_name)}
                                      className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                                      title="عرض على الخريطة"
                                    >
                                      <MapPinIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => setSelectedTransaction(transaction)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="عرض التفاصيل"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteTransaction(transaction.id)}
                                disabled={deleting === transaction.id}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                                title="حذف المعاملة"
                              >
                                {deleting === transaction.id ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <TrashIcon className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Enhanced metrics row */}
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex flex-wrap gap-2 text-xs">
                              {/* Movement metrics */}
                              {transaction.kmFromLast !== null && (
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  المسافة: {formatNumber(transaction.kmFromLast)} كم
                                </span>
                              )}
                              {transaction.odoDelta !== null && (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                  العداد: +{formatNumber(transaction.odoDelta)} كم
                                </span>
                              )}
                              {transaction.efficiencyKmPerL !== null && (
                                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                  الكفاءة: {formatNumber(transaction.efficiencyKmPerL)} كم/ل
                                </span>
                              )}
                              
                              {/* Location */}
                              {(transaction.city || transaction.district) && (
                                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                  {transaction.city && transaction.district 
                                    ? `${transaction.city} • ${transaction.district}` 
                                    : transaction.city || transaction.district}
                                </span>
                              )}

                              {/* Fraud score */}
                              {transaction.fraudScore > 0 && (
                                <span className={`px-2 py-1 rounded border ${getFraudScoreColor(transaction.fraudScore)}`}>
                                  نقاط الشك: {transaction.fraudScore}
                                </span>
                              )}
                            </div>

                            {/* Fraud flags */}
                            {transaction.fraudFlags && transaction.fraudFlags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {transaction.fraudFlags.map((flag, index) => (
                                  <span
                                    key={index}
                                    className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded border border-red-200"
                                    title={flag}
                                  >
                                    {getFraudFlagDisplayName(flag)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 bg-gray-50 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            إغلاق
          </button>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                تفاصيل معاملة الوقود #{selectedTransaction.invoice_number || 'N/A'}
              </h3>
              <button
                onClick={() => setSelectedTransaction(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto">
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">معلومات المعاملة</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم الفاتورة:</span>
                        <span className="font-medium">#{selectedTransaction.invoice_number || 'غير محدد'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">تاريخ التعبئة:</span>
                        <span className="font-medium">{formatDateTime(selectedTransaction.fill_time)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">المركبة:</span>
                        <span className="font-medium">{selectedTransaction.mappedVehiclePlate || selectedTransaction.vehicle_plate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">السائق:</span>
                        <span className="font-medium">{selectedTransaction.enrichedDriverInfo || selectedTransaction.driver || 'غير محدد'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">تفاصيل الوقود</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">الكمية:</span>
                        <span className="font-medium">{formatNumber(selectedTransaction.liters)} لتر</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">التكلفة:</span>
                        <span className="font-bold text-green-600">{formatNumber(selectedTransaction.cost)} ريال</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">السعر لكل لتر:</span>
                        <span className="font-medium">
                          {selectedTransaction.liters > 0 ?
                            formatNumber(selectedTransaction.cost / selectedTransaction.liters) : '0'} ريال/لتر
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">قراءة العداد:</span>
                        <span className="font-medium">
                          {selectedTransaction.odometer ? `${formatNumber(selectedTransaction.odometer)} كم` : 'غير محدد'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Station Information */}
                {selectedTransaction.station_name && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">معلومات المحطة</h4>
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">اسم المحطة:</span>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{selectedTransaction.station_name}</span>
                          {selectedTransaction.station_coordinates && (
                            <button
                              onClick={() => openGoogleMaps(selectedTransaction.station_coordinates, selectedTransaction.station_name)}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded"
                              title="عرض على الخريطة"
                            >
                              <MapPinIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {(selectedTransaction.city || selectedTransaction.district) && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">الموقع:</span>
                          <span className="font-medium">
                            {selectedTransaction.city && selectedTransaction.district 
                              ? `${selectedTransaction.city} • ${selectedTransaction.district}` 
                              : selectedTransaction.city || selectedTransaction.district}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Movement & Efficiency Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 border-b pb-2">معلومات الحركة والكفاءة</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">المسافة من آخر تعبئة:</span>
                      <span className="font-medium">
                        {selectedTransaction.kmFromLast !== null ? `${formatNumber(selectedTransaction.kmFromLast)} كم` : 'غير محدد'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">فرق العداد:</span>
                      <span className="font-medium">
                        {selectedTransaction.odoDelta !== null ? `${formatNumber(selectedTransaction.odoDelta)} كم` : 'غير محدد'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">الكفاءة:</span>
                      <span className="font-medium">
                        {selectedTransaction.efficiencyKmPerL !== null ? `${formatNumber(selectedTransaction.efficiencyKmPerL)} كم/لتر` : 'غير محدد'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">الوقت من آخر تعبئة:</span>
                      <span className="font-medium">
                        {selectedTransaction.hrsFromLast !== null ? `${formatNumber(selectedTransaction.hrsFromLast)} ساعة` : 'غير محدد'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Fraud Detection Results */}
                {selectedTransaction.fraudScore > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2 flex items-center">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-red-500" />
                      نتائج كشف الاحتيال
                    </h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm text-gray-600">نقاط الشك الإجمالية:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${getFraudScoreColor(selectedTransaction.fraudScore)}`}>
                          {selectedTransaction.fraudScore}
                        </span>
                      </div>
                      
                      {selectedTransaction.fraudFlags && selectedTransaction.fraudFlags.length > 0 && (
                        <div>
                          <div className="text-sm text-gray-600 mb-2">العلامات المكتشفة:</div>
                          <div className="flex flex-wrap gap-2">
                            {selectedTransaction.fraudFlags.map((flag, index) => (
                              <span
                                key={index}
                                className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded border border-red-200"
                              >
                                {getFraudFlagDisplayName(flag)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 bg-gray-50 border-t">
              <button
                onClick={() => setSelectedTransaction(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelAnalyticsModal;